// `vresume` is team-bound and uses all four levels. It is the scope where the distinction
// matters most: every collab may already edit their OWN CV with no permission at all, so a
// `vresume` row never grants entry to V-Resume — it only widens which OTHER people's CVs the
// holder reaches. view/edit = the profile's teams; view_all/edit_all = every team.
// `vlearn` (V-Learning) is team-bound and deliberately offers only THREE of the four types:
// view, edit and edit_all. There is no view_all — a read-only window onto every course in the
// company answers no question anyone asked, while edit_all (author for any team) is a real
// role. PermissionRow.js narrows the button set for this scope accordingly.
// Like `vresume`, a `vlearn` row never grants ENTRY: every collab already takes the courses
// they are enrolled in with no permission at all. The row only widens reach — view = the
// profile's teams' catalogue + training dashboard, read-only; edit = + author courses for
// those teams; edit_all = + author for any team and edit any course.
export const TEAM_BOUND_SCOPES = ['shifts', 'leaves', 'groups', 'evaluations', 'ventr', 'warnings', 'vresume', 'vlearn']
// `workStoppages` (Suivi des arrêts de travail) is GLOBAL, even though what it tracks are
// leaves. Reconciling CPAM/prévoyance reimbursements is company-wide accounting, so a
// per-team window cannot complete it — and it is a scope of its own rather than a level on
// `leaves` so that approving your own team's congés does not hand you everyone's medical
// follow-up. Keep in sync with helpers/config.js in VTalent-Synchro.
export const GLOBAL_SCOPES = ['collabs', 'settings', 'reports', 'vcomp', 'medicalVisits', 'news', 'opportunities', 'vsecurite', 'clientsTeams', 'vreference', 'workStoppages']

// ── Features hidden from the UI ───────────────────────────────────────────────
//
// Built and shipped, but not shown. Edit THIS LINE and nothing else: remove a name to
// bring that feature back, empty the array to show all three.
export const HIDDEN_FEATURES = ['vlearn', 'vreference', 'vresume'] //'vlearn', 'vreference', 'vresume'
//
// What a name here suppresses, everywhere it is checked:
//   - its entry in the sidebar (and therefore in the phone's MobileMenu, which filters the
//     same tree — a group left with nothing visible drops out on its own);
//   - its route in Body.js, so a bookmark or a typed URL lands on the same blank view an
//     unpermitted page already gives, rather than a live page with no way back to it;
//   - its row in the profile permissions grid, desktop and phone (the phone tab re-exports
//     the desktop one);
//   - 'vreference' additionally hides the "profils suggérés" box inside the Opportunity
//     modal, which reads from the referral directory.
//
// This is presentation only. Grants already in the database are left alone, the scopes stay
// in GLOBAL_SCOPES / TEAM_BOUND_SCOPES so checkPermission keeps evaluating them correctly,
// and every API behind these features stays reachable — nothing here is a security control.
// Take a name out and the feature returns with its permissions exactly as they were.
export const isFeatureHidden = (feature) => HIDDEN_FEATURES.includes(feature)

const detectEnv = () => {
    const host = (typeof window !== "undefined" && window.location && window.location.hostname) || ""
    if (host === "ppdv365.groupe-vital.com") return "PREPROD"
    if (host === "v365.groupe-vital.com") return "PROD"
    return "STAG" // localhost / dev
}

// Fixed set the backend now validates news.category against — kept in sync with the
// backend's list rather than derived from existing posts. Each value is the canonical
// string stored in the DB; NEWS_CATEGORY_LANG_KEYS maps it to its translation key so the
// same stored value renders localized everywhere it's shown (table, popup, create form).
export const NEWS_CATEGORIES = ['General', 'HR', 'Urgent', 'Planning', 'Payroll', 'Events', 'Security', 'System', 'Client', 'Organisation', 'Sponsoring']
export const NEWS_CATEGORY_LANG_KEYS = {
    General: 'NEWS_CATEGORY_GENERAL',
    HR: 'NEWS_CATEGORY_HR',
    Urgent: 'NEWS_CATEGORY_URGENT',
    Planning: 'NEWS_CATEGORY_PLANNING',
    Payroll: 'NEWS_CATEGORY_PAYROLL',
    Events: 'NEWS_CATEGORY_EVENTS',
    Security: 'NEWS_CATEGORY_SECURITY',
    System: 'NEWS_CATEGORY_SYSTEM',
    Client: 'NEWS_CATEGORY_CLIENT',
    Organisation: 'NEWS_CATEGORY_ORGANISATION',
    Sponsoring: 'NEWS_CATEGORY_SPONSORING',
}
// Who a news post is addressed to. The two keywords the backend understands as targets
// (helpers/news/audience.js) — everything else in the picker is a manager profile id.
// 'all' is stored on `news.audience` itself and means the post has no target rows at all;
// 'collabs' is a target row and names the collabs holding no manager profile.
export const NEWS_AUDIENCE_ALL = 'all'
export const NEWS_AUDIENCE_COLLABS = 'collabs'
export const NEWS_AUDIENCE_PROFILE = 'profile'

// Explicit per-category color, rather than the generic string-hash palette: keeps every
// category visually stable and lets Urgent stand out as a solid, unmissable red instead of
// the same light tint as everything else.
export const NEWS_CATEGORY_COLORS = {
    General: { color: '#546E7A', background: 'rgba(84, 110, 122, 0.10)' },
    HR: { color: '#8E24AA', background: 'rgba(142, 36, 170, 0.10)' },
    Urgent: { color: '#FFFFFF', background: '#D32F2F', border: '#B71C1C' },
    Planning: { color: '#00838F', background: 'rgba(0, 131, 143, 0.10)' },
    Payroll: { color: '#2E7D32', background: 'rgba(46, 125, 50, 0.10)' },
    Events: { color: '#B06000', background: 'rgba(255, 144, 21, 0.14)' },
    Security: { color: '#5D4037', background: 'rgba(93, 64, 55, 0.10)' },
    System: { color: '#37474F', background: 'rgba(55, 71, 79, 0.10)' },
    Client: { color: '#0B5FA5', background: 'rgba(11, 95, 165, 0.10)' },
    // Picked against the nine above rather than by eye: both sit further from every
    // existing category than the closest existing pair does from each other, so neither
    // makes the set harder to read, and both clear 4.5:1 on their own tint for the chip
    // label. Deep purple is the free hue between HR's purple and Client's blue; deep
    // magenta is the free one between Urgent's red and HR's purple.
    Organisation: { color: '#4A148C', background: 'rgba(74, 20, 140, 0.10)' },
    Sponsoring: { color: '#880E4F', background: 'rgba(136, 14, 79, 0.10)' },
}

export const config = {
    env: detectEnv(), // auto-detected from hostname: ppdv365→PREPROD, v365→PROD, else STAG. No per-branch edits.
    tenantUrl: "https://login.microsoftonline.com/groupevital.com",
    clientId: "c6701e56-6f31-4a76-af87-92dd0c8848a0",
    timezone: "Europe/Paris",
    scopes: {
        collabs: "collabs",
        shifts: "shifts",
        leaves: "leaves",
        groups: "groups",
        settings: "settings",
        reports: "reports",
        warnings: "warnings",
        evaluations: "evaluations",
        vcomp: "vcomp",
        ventr: "ventr",
        medicalVisits: "medicalVisits",
        news: "news",
        opportunities: "opportunities",
        vsecurite: "vsecurite",
        clientsTeams: "clientsTeams",
        vreference: "vreference",
        vresume: "vresume",
        vlearn: "vlearn",
        workStoppages: "workStoppages"
    },
    permissionTypes: {
        view: "view",
        edit: "edit",
        remove: "remove",
        view_all: "view_all",
        edit_all: "edit_all"
    },
    apiPaths: {
        user: {
            login: "/user/login",
            session: "/user/session",
            logout: "/user/logout",
            msAuth: "/ms-auth",
            sendActivationLink: "/user/sendActivationLink",
            setPassword: "/user/setPassword",
        },
        planning: {
            getHolidays: "/v1/planning/getHolidays",
            getTeams: "/v1/planning/getTeams",
            addGroupe: "/v1/planning/addGroupe",
            getGroupes: "/v1/planning/getGroupes",
            updateGroupe: "/v1/planning/updateGroupe",
            deleteGroupes: "/v1/planning/deleteGroupes",
            getCollabs: "/v1/planning/getCollabs",
            getAllCollabs: "/v1/planning/getAllCollabs",
            resetCollabPassword: "/v1/planning/resetCollabPass",
            updateCollabs: "/v1/planning/updateCollabs",
            mergeCollabs: "/v1/planning/mergeCollabs",
            getMembers: "/v1/planning/getMembers",
            addGroupeMembers: "/v1/planning/addGroupeMembers",
            removeGroupeMembers: "/v1/planning/removeGroupeMembers",
            getSettings: "/v1/planning/getSettings",
            updateSettings: "/v1/planning/updateSettings",
            getTeamSettings: "/v1/planning/getTeamSettings",
            updateTeamSettings: "/v1/planning/updateTeamSettings",
            addShiftSample: "/v1/planning/addShiftSample",
            getShiftSamples: "/v1/planning/getShiftSamples",
            deleteShiftSamples: "/v1/planning/deleteShiftSamples",
            updateShiftSample: "/v1/planning/updateShiftSample",
            getPlanning: "/v1/planning/getPlanning",
            getMyLeaves: "/v1/planning/getMyLeaves",
            addShifts: "/v1/planning/addShifts",
            updateShifts: "/v1/planning/updateShifts",
            deleteShifts: "/v1/planning/deleteShifts",
            prefill: "/v1/planning/prefill",
            delete: "/v1/planning/delete",
            getClients: "/v1/planning/getClients",
            submitShifts: "/v1/planning/submitShifts",
            addLeaveSample: "/v1/planning/addLeaveSample",
            getLeaveSamples: "/v1/planning/getLeaveSamples",
            deleteLeaveSamples: "/v1/planning/deleteLeaveSamples",
            updateLeaveSample: "/v1/planning/updateLeaveSample",
            requestLeave: "/v1/planning/requestLeave",
            submitLeave: "/v1/planning/submitLeave",
            deleteLeave: "/v1/planning/deleteLeave",
            requestDeleteLeave: "/v1/planning/requestDeleteLeave",
            listLeaveAttachments: "/v1/planning/leaveAttachments/list",
            uploadLeaveAttachment: "/v1/planning/leaveAttachments/upload",
            downloadLeaveAttachment: "/v1/planning/leaveAttachments/download",
            deleteLeaveAttachment: "/v1/planning/leaveAttachments/delete",
            getLeaveAdjustments: "/v1/planning/getLeaveAdjustments",
            addLeaveAdjustment: "/v1/planning/addLeaveAdjustment",
            removeLeaveAdjustment: "/v1/planning/removeLeaveAdjustment",
            getTimesheets: "/v1/planning/getTimesheets",
            getCollabPlanning: "/v1/planning/getCollabPlanning",
            updateTimesheet: "/v1/planning/updateTimesheet",
            updateCollabsOrder: "/v1/planning/updateCollabsOrder",
            getReports: "/v1/planning/getReports",
            generateReport: "/v1/planning/generateReport",
            getCollabReport: "/v1/planning/getCollabReport",
            generateCraPdf: "/v1/planning/generateCraPdf",
            generateCraZip: "/v1/planning/generateCraZip",
            stopZipGeneration: "/v1/planning/stopZipGeneration",
            zipGenerationProgress: "/v1/planning/zipGenerationProgress",
            insertPrimeCollab: "/v1/planning/insertPrimeCollab",
            getAllWarnings: "/v1/planning/getWarnings",
            deleteWarning: "/v1/planning/deleteWarning",
            editWarnings: "/v1/planning/editWarnings",
            addWarning: "/v1/planning/addWarning",
            getEvaluations: "/v1/planning/getEvaluations",
            deleteEvaluation: "/v1/planning/deleteEvaluation",
            addEvaluation: "/v1/planning/addEvaluation",
            editEvaluation: "/v1/planning/editEvaluation",
            getEvaluationFields: "/v1/planning/getEvaluationFields",
            getEvaluationsReport: "/v1/planning/getEvaluationsReport",
            getMonthlyEvaluations: "/v1/planning/getMonthlyEvaluations",
            deleteEvaluationField: "/v1/planning/deleteEvaluationField",
            addEvaluationField: "/v1/planning/addEvaluationField",
            getEvaluationsTypes: "/v1/planning/getEvaluationsTypes",
            reorderEvaluationsFields: "/v1/planning/reorderEvaluationsFields",
            reorderEvaluationTypes: "/v1/planning/reorderEvaluationTypes",
            editEvaluationField: "/v1/planning/editEvaluationField",
            addEvaluationsType: "/v1/planning/addEvaluationsType",
            editEvaluationsType: "/v1/planning/editEvaluationsType",
            getCompanies: "/v1/planning/getCompanies",
            getSingleCollabDetails: "/v1/planning/getSingleCollabDetails",
            getPostes: "/v1/planning/getPostes",
            getTeamCollabs: "/v1/planning/getTeamCollabs",
            addEditClient: "/v1/planning/addEditClient",
            generateAnnualPrimeReportByType: "/v1/planning/generateAnnualPrimeReportByType",
            getEvaluationTypeConfig: "/v1/planning/getEvaluationTypeConfig",
            saveEvaluationTypeConfig: "/v1/planning/saveEvaluationTypeConfig",
            getSpecialCases: "/v1/planning/getSpecialCases",
            addSpecialCase: "/v1/planning/addSpecialCase",
            editSpecialCase: "/v1/planning/editSpecialCase",
            getCollabsMissionsWithinTeam: "/v1/planning/getCollabsMissionsWithinTeam",
            getInactiveClients: "/v1/planning/getInactiveClients",
            addFavoriteShiftSample: "/v1/planning/addFavoriteShiftSample",
            deleteFavoriteShiftSample: "/v1/planning/deleteFavoriteShiftSample",
            addFavoriteLeaveSample: "/v1/planning/addFavoriteLeaveSample",
            deleteFavoriteLeaveSample: "/v1/planning/deleteFavoriteLeaveSample"
        },
        vcomp: {
            addEditVcompProject: "/v1/vcomp/addEditVcompProject",
            deleteProject: "/v1/vcomp/deleteProject",
            getBankAptitudesOnCreation: "/v1/vcomp/getBankAptitudesOnCreation",
            addDefaultProjectForm: "/v1/vcomp/addDefaultProjectForm",
            reorderProjectForm: "/v1/vcomp/reorderProjectForm",
            getProjects: "/v1/vcomp/getProjects",
            addEditCategory: "/v1/vcomp/addEditCategory",
            addEditAptitude: "/v1/vcomp/addEditAptitude",
            getProjectAptitudes: "/v1/vcomp/getProjectAptitudes",
            deleteProjectCatSub: "/v1/vcomp/deleteProjectCatSub",
            deleteProjectAptitude: "/v1/vcomp/deleteProjectAptitude",
            getProjectMembersAndManagers: "/v1/vcomp/getProjectMembersAndManagers",
            getCollabsNames: "/v1/vcomp/getCollabsNames",
            addRemoveProjectCollabsManagers: "/v1/vcomp/addRemoveProjectCollabsManagers",
            reorderPublicForm: "/v1/vcomp/reorderPublicForm",
            hidePublicCatSub: "/v1/vcomp/hidePublicCatSub",
            hidePublicAptitudes: "/v1/vcomp/hidePublicAptitudes",
            getAptitudeQuestions: "/v1/vcomp/getAptitudeQuestions",
            getProjectAptitudesWithValues: "/v1/vcomp/getProjectAptitudesWithValues",
            answerAptitudeDailyBase: "/v1/vcomp/answerAptitudeDailyBase",
            getProjectProgress: "/v1/vcomp/getProjectProgress",
            editResponseManager: "/v1/vcomp/editResponseManager",
            activateMcqCollabs: "/v1/vcomp/activateMcqCollabs",
            addEditQuestion: "/v1/vcomp/addEditQuestion",
            addEditQuestionAnswers: "/v1/vcomp/addEditQuestionAnswers",
            deleteQuestion: "/v1/vcomp/deleteQuestion",
            deleteQuestionAnswer: "/v1/vcomp/deleteQuestionAnswer",
            aptitudeMcqVisibility: "/v1/vcomp/aptitudeMcqVisibility",
            getProjectQuestions: "/v1/vcomp/getProjectQuestions",
            recordMcqResponsePerProject: "/v1/vcomp/recordMcqResponsePerProject",
            getCollabMcqResults: "/v1/vcomp/getCollabMcqResults",
            importProjectFromExcel: "/v1/vcomp/importProjectFromExcel",
            getCollabResponseHistory: "/v1/vcomp/getCollabResponseHistory",
            getProjectQuestionsBank: "/v1/vcomp/getProjectQuestionsBank",
            deleteVcompProjectsWithConnections: "/v1/vcomp/deleteVcompProjectsWithConnections",
            updateNeededCollabsPerSession: "/v1/vcomp/updateNeededCollabsPerSession"
        },
        ventr: {
            getInterviews: "/v1/entretiens/getInterviews",
            insertInterview: "/v1/entretiens/insertInterview",
            updateInterview: "/v1/entretiens/updateInterview",
            generateInterviewPDF: "/v1/entretiens/generateInterviewPDF"
        },
        managersProfiles: {
            getProfiles: "/v1/managersProfiles/getProfiles",
            addProfile: "/v1/managersProfiles/addProfile",
            editProfile: "/v1/managersProfiles/editProfile",
            getPermissions: "/v1/managersProfiles/getPermissions",
            updatePermissions: "/v1/managersProfiles/updatePermissions",
            getManagersList: "/v1/managersProfiles/getManagersList",
            getTeamsManagersList: "/v1/managersProfiles/getTeamsManagersList",
        },
        teams: {
            addRemoveMembers: "/v1/teams/addRemoveMembers",
            getTeamsManagers: "/v1/teams/getTeamsManagers",
            getAllTeamsManagers: "/v1/teams/getAllTeamsManagers",
            addTeamManager: "/v1/teams/addTeamManager",
            editTeam: "/v1/teams/editTeam",
            addTeam: "/v1/teams/addTeam",
            removeTeamManager: "/v1/teams/removeTeamManager",
            getPostesSettings: "/v1/teams/getPostesSettings",
            addEditPoste: "/v1/teams/addEditPoste",
            removePoste: "/v1/teams/removePoste",
            getOpportunities: "/v1/teams/getOpportunities",
            addOpportunity: "/v1/teams/addOpportunity",
            getCollabMissions: "/v1/teams/getCollabMissions",
            getAllMissions: "/v1/teams/getAllMissions",
            editCollabMission: "/v1/teams/editCollabMission",
            countShiftsAfterMissionEnd: "/v1/teams/countShiftsAfterMissionEnd",
            addCollabMission: "/v1/teams/addCollabMission",
            deleteCollabMission: "/v1/teams/deleteCollabMission",
            editCollabProfile: "/v1/teams/editCollabProfile",
            editOpportunity: "/v1/teams/editOpportunity",
            getEndingContracts: "/v1/teams/getEndingContracts",
            changeTeamActiveStatus: "/v1/teams/changeTeamActiveStatus",
            getInActiveTeams: "/v1/teams/getInActiveTeams"
        },
        clientsTeams: {
            getClients: "/v1/clientsTeams/getClients",
            addEditClient: "/v1/clientsTeams/addEditClient",
            getInactiveClients: "/v1/clientsTeams/getInactiveClients",
            getTeamsManagers: "/v1/clientsTeams/getTeamsManagers",
            getAllTeamsManagers: "/v1/clientsTeams/getAllTeamsManagers",
            addTeamManager: "/v1/clientsTeams/addTeamManager",
            removeTeamManager: "/v1/clientsTeams/removeTeamManager",
            addTeam: "/v1/clientsTeams/addTeam",
            editTeam: "/v1/clientsTeams/editTeam",
            changeTeamActiveStatus: "/v1/clientsTeams/changeTeamActiveStatus",
            getInActiveTeams: "/v1/clientsTeams/getInActiveTeams",
            getCollabMissions: "/v1/clientsTeams/getCollabMissions",
            getAllMissions: "/v1/clientsTeams/getAllMissions",
        },
        subcontractors: {
            getSubcontractors: "/v1/subcontractors/getSubcontractors",
            addSubcontractor: "/v1/subcontractors/addSubcontractor",
            editSubcontractor: "/v1/subcontractors/editSubcontractor",
            getSubcontractorCollabs: "/v1/subcontractors/getSubcontractorCollabs",
            addCollabsToSubcontractor: "/v1/subcontractors/addCollabsToSubcontractor",
            removeCollabsFromSubcontractor: "/v1/subcontractors/removeCollabsFromSubcontractor",
            getSkillsSettings: "/v1/subcontractors/getSkillsSettings",
            addEditSkill: "/v1/subcontractors/addEditSkill",
            removeSkill: "/v1/subcontractors/removeSkill"
        },
        assets: {
            getCollabAssets: "/v1/assets/getCollabAssets",
            getSubcontractorAssets: "/v1/assets/getSubcontractorAssets",
            uploadAsset: "/v1/assets/uploadAsset",
            downloadAsset: "/v1/assets/download",
            deleteAsset: "/v1/assets/delete",
        },
        collabs: {
            addCollab: "/v1/collabs/addCollab",
            editCollab: "/v1/collabs/editCollab",
            addCollabContract: "/v1/collabs/addCollabContract",
            editCollabContract: "/v1/collabs/editCollabContract",
            getCollabContract: "/v1/collabs/getCollabContract",
            deleteCollabContract: "/v1/collabs/deleteCollabContract",
            getAllContracts: "/v1/collabs/getAllContracts",
            updateWeeklyReview: "/v1/collabs/updateWeeklyReview",
            getAllWeeklyReviews: "/v1/collabs/getAllWeeklyReviews",
            setMyPhoto: "/v1/collabs/setMyPhoto",
            removeMyPhoto: "/v1/collabs/removeMyPhoto",
            // Acting on another collab's picture — both require `collabs` edit.
            setCollabPhoto: "/v1/collabs/setCollabPhoto",
            removeCollabPhoto: "/v1/collabs/removeCollabPhoto",
            // Not fetched through NetworkManager: this one is consumed as an <img src>, so
            // it is built into a URL by getCollabPhotoUrl() in utils/helpers.js.
            photo: "/v1/collabs/photo"
        },
        medicalVisits: {
            getAll: "/v1/medicalVisits/getAll",
            update: "/v1/medicalVisits/update"
        },
        workStoppages: {
            getAll: "/v1/workStoppages/getAll",
            get: "/v1/workStoppages/get",
            getHistory: "/v1/workStoppages/getHistory",
            getDeleted: "/v1/workStoppages/getDeleted",
            getReference: "/v1/workStoppages/getReference",
            getEligibleLeaves: "/v1/workStoppages/getEligibleLeaves",
            add: "/v1/workStoppages/add",
            edit: "/v1/workStoppages/edit",
            delete: "/v1/workStoppages/delete",
            addPayment: "/v1/workStoppages/addPayment",
            editPayment: "/v1/workStoppages/editPayment",
            deletePayment: "/v1/workStoppages/deletePayment",
            rejectLeave: "/v1/workStoppages/rejectLeave",
            listAttachments: "/v1/workStoppages/attachments/list",
            uploadAttachment: "/v1/workStoppages/attachments/upload",
            downloadAttachment: "/v1/workStoppages/attachments/download",
            deleteAttachment: "/v1/workStoppages/attachments/delete",
            // Self-service: the signed-in collab's own arrêts (no scope; ownership-checked).
            getMine: "/v1/workStoppages/my/getAll",
            requestMine: "/v1/workStoppages/my/request",
            editMine: "/v1/workStoppages/my/edit",
            cancelMine: "/v1/workStoppages/my/cancel",
            listMyAttachments: "/v1/workStoppages/my/attachments/list",
            uploadMyAttachment: "/v1/workStoppages/my/attachments/upload",
            downloadMyAttachment: "/v1/workStoppages/my/attachments/download",
            deleteMyAttachment: "/v1/workStoppages/my/attachments/delete"
        },
        vreference: {
            getAll: "/v1/vreference/getAll",
            search: "/v1/vreference/search",
            checkDuplicates: "/v1/vreference/checkDuplicates",
            getPostes: "/v1/vreference/getPostes",
            suggestions: "/v1/vreference/suggestions",
            profile: "/v1/vreference/profile",
            add: "/v1/vreference/add",
            update: "/v1/vreference/update",
            updateStatus: "/v1/vreference/updateStatus",
            delete: "/v1/vreference/delete",
            listAttachments: "/v1/vreference/attachments/list",
            uploadAttachment: "/v1/vreference/attachments/upload",
            downloadAttachment: "/v1/vreference/attachments/download",
            deleteAttachment: "/v1/vreference/attachments/delete"
        },
        vresume: {
            get: "/v1/vresume/get",
            list: "/v1/vresume/list",
            getPostes: "/v1/vresume/getPostes",
            save: "/v1/vresume/save",
            saveExperience: "/v1/vresume/saveExperience",
            deleteExperience: "/v1/vresume/deleteExperience",
            reorderExperiences: "/v1/vresume/reorderExperiences",
            sync: "/v1/vresume/sync",
            // Answers a PDF stream, not JSON — consumed through
            // NetworkManager.fetchFile(), never postRequest().
            exportPdf: "/v1/vresume/exportPdf"
        },
        vlearn: {
            // Module configuration. `settingsGet` is readable by everyone (the authoring UI
            // needs video_source to know which lesson types to offer); `settingsUpdate` is
            // admins only, enforced in VLearnController.
            settingsGet: "/v1/vlearn/settings/get",
            settingsUpdate: "/v1/vlearn/settings/update",
            categoriesList: "/v1/vlearn/categories/list",
            categoriesSave: "/v1/vlearn/categories/save",
            categoriesDelete: "/v1/vlearn/categories/delete",

            coursesList: "/v1/vlearn/courses/list",
            coursesGet: "/v1/vlearn/courses/get",
            coursesSave: "/v1/vlearn/courses/save",
            coursesPublish: "/v1/vlearn/courses/publish",
            coursesUnpublish: "/v1/vlearn/courses/unpublish",
            coursesArchive: "/v1/vlearn/courses/archive",
            coursesDelete: "/v1/vlearn/courses/delete",
            coursesDuplicate: "/v1/vlearn/courses/duplicate",

            audienceTargets: "/v1/vlearn/audience/targets",
            audienceGet: "/v1/vlearn/audience/get",
            audienceSave: "/v1/vlearn/audience/save",
            audienceRefresh: "/v1/vlearn/audience/refresh",

            managersCandidates: "/v1/vlearn/managers/candidates",
            managersAdd: "/v1/vlearn/managers/add",
            managersRemove: "/v1/vlearn/managers/remove",
            managersTransferOwnership: "/v1/vlearn/managers/transferOwnership",

            sectionsSave: "/v1/vlearn/sections/save",
            sectionsDelete: "/v1/vlearn/sections/delete",
            sectionsReorder: "/v1/vlearn/sections/reorder",
            lessonsSave: "/v1/vlearn/lessons/save",
            lessonsDelete: "/v1/vlearn/lessons/delete",
            lessonsReorder: "/v1/vlearn/lessons/reorder",
            lessonsMove: "/v1/vlearn/lessons/move",

            // Multipart. The parent id travels in the QUERY STRING, not the body — multer
            // does not parse the body until it runs, and authorising after the bytes have
            // landed on disk would be too late. Called through
            // NetworkManager.uploadVLearnFile, never postRequest.
            filesUpload: "/v1/vlearn/files/upload",
            filesDelete: "/v1/vlearn/files/delete",
            // GET endpoints used directly as <iframe src> / <video src> / <img src>.
            // Build them with NetworkManager.getVLearnFileUrl / getVLearnStreamUrl /
            // getVLearnCoverUrl — never fetch them as JSON.
            filesDownload: "/v1/vlearn/files/download",
            filesStream: "/v1/vlearn/files/stream",
            coverUpload: "/v1/vlearn/courses/uploadCover",
            coverDownload: "/v1/vlearn/courses/cover",

            openLesson: "/v1/vlearn/progress/openLesson",
            heartbeat: "/v1/vlearn/progress/heartbeat",
            completeLesson: "/v1/vlearn/progress/completeLesson",

            dashboardCourse: "/v1/vlearn/dashboard/course",
            dashboardLearners: "/v1/vlearn/dashboard/learners",
            dashboardLessons: "/v1/vlearn/dashboard/lessons",
            dashboardActivity: "/v1/vlearn/dashboard/activity",
            dashboardCollab: "/v1/vlearn/dashboard/collab",
            // Answers an Excel stream — NetworkManager.fetchFile, never postRequest.
            dashboardExport: "/v1/vlearn/dashboard/export",

            announcementsList: "/v1/vlearn/announcements/list",
            announcementsSave: "/v1/vlearn/announcements/save",
            announcementsDelete: "/v1/vlearn/announcements/delete",
            announcementsMarkRead: "/v1/vlearn/announcements/markRead",

            // Answers a PDF stream — NetworkManager.fetchFile, never postRequest.
            certificateDownload: "/v1/vlearn/certificate/download",

            quizzesList: "/v1/vlearn/quizzes/list",
            quizzesSave: "/v1/vlearn/quizzes/save",
            quizzesDelete: "/v1/vlearn/quizzes/delete",
            questionsList: "/v1/vlearn/quizzes/questions/list",
            questionsSave: "/v1/vlearn/quizzes/questions/save",
            questionsDelete: "/v1/vlearn/quizzes/questions/delete",
            questionsReorder: "/v1/vlearn/quizzes/questions/reorder",
            questionsImport: "/v1/vlearn/quizzes/questions/import",

            quizForLearner: "/v1/vlearn/quizzes/getForLearner",
            attemptStart: "/v1/vlearn/quizzes/attempt/start",
            attemptSubmit: "/v1/vlearn/quizzes/attempt/submit",
            attemptReview: "/v1/vlearn/quizzes/attempt/review",
            attemptList: "/v1/vlearn/quizzes/attempt/list"
        },
        dashboard: {
            getCollabData: "/v1/dashboard/getCollabData",
            addNews: "/v1/dashboard/addNews",
            getVcompNotifications: "/v1/dashboard/getVcompNotifications",
            markNewsAsRead: "/v1/dashboard/markNewsAsRead",
            deleteNews: "/v1/dashboard/deleteNews",
            getNewsViewers: "/v1/dashboard/getNewsViewers",
            getNewsAudienceOptions: "/v1/dashboard/getNewsAudienceOptions",
            listNewsAttachments: "/v1/dashboard/newsAttachments/list",
            uploadNewsAttachment: "/v1/dashboard/newsAttachments/upload",
            downloadNewsAttachment: "/v1/dashboard/newsAttachments/download",
            deleteNewsAttachment: "/v1/dashboard/newsAttachments/delete"
        },
        employmentPromises: {
            getAll: "/v1/employmentPromises/getAll",
            insert: "/v1/employmentPromises/insert",
            update: "/v1/employmentPromises/update",
            delete: "/v1/employmentPromises/delete"
        },
        vsecurite: {
            fetchAwarenessTeams: "/v1/vsecurite/awareness/teams",
            fetchAwarenessCollabs: "/v1/vsecurite/awareness/collabs",
            createAwarenessCampaign: "/v1/vsecurite/awareness/create",
            fetchAwarenessCampaigns: "/v1/vsecurite/awareness/list",
            fetchAwarenessCampaign: "/v1/vsecurite/awareness/get",
            updateAwarenessCampaign: "/v1/vsecurite/awareness/update",
            deleteAwarenessCampaign: "/v1/vsecurite/awareness/delete",
            publishAwarenessCampaign: "/v1/vsecurite/awareness/publish",
            fetchAwarenessTracking: "/v1/vsecurite/awareness/tracking",
            exportAwarenessTracking: "/v1/vsecurite/awareness/tracking/export",
            fetchCollabPendingAwareness: "/v1/vsecurite/awareness/collab/pending",
            fetchCollabAwarenessHistory: "/v1/vsecurite/awareness/collab/history",
            acknowledgeAwareness: "/v1/vsecurite/awareness/acknowledge",
            resetAcknowledgement: "/v1/vsecurite/awareness/acknowledge/reset",
            resendAwarenessReminder: "/v1/vsecurite/awareness/resend-reminder",
            createQuiz: "/v1/vsecurite/quizzes/create",
            fetchQuizzes: "/v1/vsecurite/quizzes/list",
            fetchQuiz: "/v1/vsecurite/quizzes/get",
            updateQuiz: "/v1/vsecurite/quizzes/update",
            deleteQuiz: "/v1/vsecurite/quizzes/delete",
            publishQuiz: "/v1/vsecurite/quizzes/publish",
            fetchQuizQuestions: "/v1/vsecurite/quizzes/questions/list",
            addQuestion: "/v1/vsecurite/quizzes/questions/add",
            updateQuestion: "/v1/vsecurite/quizzes/questions/update",
            deleteQuestion: "/v1/vsecurite/quizzes/questions/delete",
            importQuestions: "/v1/vsecurite/quizzes/questions/import",
            fetchQuizTracking: "/v1/vsecurite/quizzes/tracking",
            fetchAdminCollabReview: "/v1/vsecurite/quizzes/admin/collab-review",
            exportQuizTracking: "/v1/vsecurite/quizzes/tracking/export",
            fetchCollabPendingQuizzes: "/v1/vsecurite/quizzes/collab/pending",
            fetchCollabQuizHistory: "/v1/vsecurite/quizzes/collab/history",
            fetchQuizReview: "/v1/vsecurite/quizzes/collab/review",
            fetchCollabQuizAttempts: "/v1/vsecurite/quizzes/collab/attempts",
            startQuizAttempt: "/v1/vsecurite/quizzes/attempt/start",
            submitAnswer: "/v1/vsecurite/quizzes/attempt/answer",
            finishAttempt: "/v1/vsecurite/quizzes/attempt/finish",
            fetchAttemptResults: "/v1/vsecurite/quizzes/attempt/results",
            fetchDashboardSummary: "/v1/vsecurite/dashboard/summary",
            fetchDashboardByTeam: "/v1/vsecurite/dashboard/by-team",
            fetchDashboardExport: "/v1/vsecurite/dashboard/export",
        }
    }
}
